import * as React from 'react';
import { ArrowSquareOut, CheckCircle } from '@liveauctioneers/hammer-ui-core/icons';
import { Caption } from '@liveauctioneers/hammer-ui-core/text';
import { FormattedMessage } from '@liveauctioneers/hammer-ui-core/intl';
import { getCatalogDetailExternalUrl } from '@/redux/modules/catalog/detail/catalogDetail.selectors';
import { getCatalogPaymentProvider } from '@/redux/modules/catalog/catalogs/catalog.selectors';
import { getDefaultBidderCreditCardId } from '@/redux/modules/account/payments/creditCard/creditCard/creditCard.selectors';
import { getItemDetailExternalUrl } from '@/redux/modules/itemDetail';
import {
    getRegistrationApproved,
    getRegistrationPending,
} from '@/redux/modules/catalog/registration/catalogRegistration.selectors';
import { useAppSelector } from '@/redux/hooks';
import Button from '@liveauctioneers/caterwaul-components/lib/Button/Button';
import isEqual from 'react-fast-compare';
import ShouldLogInContainer from '@/components/ShouldLogIn/ShouldLogInContainer';
import styled from 'styled-components';

type Props = {
    catalogId: number;
    className?: string;
    itemId?: number;
    submitRegisterForCatalog: Function;
    text?: React.ReactNode;
};

const RequestOnlineBiddingButton = ({ catalogId, className, itemId, submitRegisterForCatalog, text = '' }: Props) => {
    /** Only show the green request_submitted button if approved/pending AND the button has been clicked */
    const [hasClickedButton, setHasClickedButton] = React.useState<boolean>(false);
    const approved = useAppSelector((state) => getRegistrationApproved(state, catalogId));
    const pending = useAppSelector((state) => getRegistrationPending(state, catalogId));
    const catalogExternalUrl = useAppSelector((state) => getCatalogDetailExternalUrl(state, catalogId));
    const itemExternalUrl = useAppSelector((state) => getItemDetailExternalUrl(state, itemId));
    const paymentProvider = useAppSelector((state) => getCatalogPaymentProvider(state, catalogId));
    const cardId = useAppSelector((state) => getDefaultBidderCreditCardId(state, paymentProvider));

    const externalUrl = itemId ? itemExternalUrl : catalogExternalUrl;

    return (
        <>
            {(approved || pending) && hasClickedButton ? (
                <StyledRequested
                    className={className}
                    disabled
                    green
                    secondary
                >
                    <CheckCircle
                        className="mr-2xs"
                        color="success"
                        size="sm"
                        style="fill"
                    />
                    <FormattedMessage id="request_submitted" />
                </StyledRequested>
            ) : (
                <ShouldLogInContainer
                    requireCompleteProfile
                    requireLogin
                    runFunction={(args) => {
                        setHasClickedButton(true);
                        submitRegisterForCatalog(args);
                        if (Boolean(externalUrl)) {
                            window.open(externalUrl, '_blank');
                        }
                    }}
                    runFunctionArgument={{ cardId, catalogId, itemId }}
                >
                    {Boolean(externalUrl) ? (
                        <StyledExternalButton
                            className={className}
                            primary
                        >
                            {Boolean(text) ? text : <FormattedMessage id="register_partners_site" />}
                            <ArrowSquareOut
                                className="ml-2xs"
                                size="sm"
                                style="fill"
                            />
                        </StyledExternalButton>
                    ) : (
                        <StyledButton
                            className={className}
                            primary
                        >
                            {Boolean(text) ? text : <FormattedMessage id="request_online_bidding" />}
                        </StyledButton>
                    )}
                </ShouldLogInContainer>
            )}
            {Boolean(externalUrl) && (
                <Caption
                    className="mb-[30px]"
                    color="secondary"
                >
                    <FormattedMessage id="register_partners_site_warning" />
                </Caption>
            )}
        </>
    );
};

export default React.memo<Props>(RequestOnlineBiddingButton, isEqual);

const StyledRequested = styled(Button)`
    width: 100%;

    &:hover {
        color: ${({ theme }) => theme.colors.white};
    }
`;

const StyledExternalButton = styled(Button)`
    min-width: 100%;
    margin-bottom: 15px;
`;

const StyledButton = styled(Button)`
    min-width: 100%;
`;
